import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Location } from '@angular/common';
import { Store } from '@ngrx/store';
import { TrialDetail } from 'app/core/models/trial-detail.model';
import { TrialService } from 'app/core/services/trial.service';
import { NavigationState } from 'app/core/state/navigation.state';
import { LogHelper } from 'app/core/helpers/log.helper';
import { AuthService } from 'app/core/services/auth.service';
import { AlertService } from 'app/shared/alert/alert.service';
import { UpdateLastRouteAction } from 'app/core/actions/navigation.action';
import { TemplateService } from 'app/core/services/template.service';
import { ModalComponent } from 'app/shared/modal/modal.component';
import { TrialConstants } from 'app/core/constants/trial-constants';
import { Permissions } from '../../../core/constants/permissions';
import { RoleNames } from "../../../core/constants/role-names";
import { TrialAdminUser } from "../../../core/models/trial-admin-user.model";
import { BadgeStyle } from "../../../shared/badge/badge-style.enum";
import { BadgeSize } from "../../../shared/badge/badge-size.enum";
import { TrialSupportComponent } from "./trial-support/trial-support.component";
import { NavigationService, NavRoute } from "../../../core/services/navigation.service";
import { DiaryFormService } from "../../../core/services/diary-form.service";
import { DiaryFormSummary } from "../../../core/models/trial-diary-form/diary-form-summary.model";

@Component({
  selector: 'app-trial-details',
  templateUrl: './trial-details.component.html',
  styleUrls: ['./trial-details.component.scss']
})
export class TrialDetailsComponent implements OnInit, AfterViewInit {
  // Export
  @ViewChild('dateFrom') dateFrom: ElementRef;
  @ViewChild('dateTo') dateTo: ElementRef;
  @ViewChild('exportTripsModal') exportTripsModal: ModalComponent;
  @ViewChild('contactModal') contactModal: ModalComponent;
  @ViewChild('supportComponent') supportComponent: TrialSupportComponent;

  exportIsProcessing = false;
  exportForm = new UntypedFormGroup({
    dateFrom: new UntypedFormControl(),
    dateTo: new UntypedFormControl(),
    type: new UntypedFormControl('default'),
    site: new UntypedFormControl(),
    allData: new UntypedFormControl(false),
    allSites: new UntypedFormControl(false),
    diaryFormId: new UntypedFormControl()
  });
  diaryForms: DiaryFormSummary[] = [];
  Permissions = Permissions;

  trial = new TrialDetail();
  dataLoaded: boolean = false;
  loaderArray: number[] = [1,1,1,1];
  selectedTab = 'sites';
  preSelectedTab = '';
  trialInfoTabEnabled = TrialConstants.trialInfoTabEnabled;
  supportContactsHasChanged: boolean = false;

  payments = false;
  therapeuticAreaOptions = TrialConstants.therapeuticAreaOptions;
  roleNames = RoleNames;
  exportOptions: { value: string, text: string }[] = [];

  constructor(
    private navService: NavigationService,
    public authService: AuthService,
    private readonly trialService: TrialService,
    private readonly diaryFormService: DiaryFormService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly templateService: TemplateService,
    private readonly alertService: AlertService,
    private readonly location: Location,
    private readonly store: Store<NavigationState>) {
  }

  ngOnInit() {
    this.templateService.showHeader();

    this.activatedRoute.params.subscribe(params => {
      this.loadTrial(params.id);
    });

    // Reset dates when all data is selected
    this.exportForm.get('allData').valueChanges.subscribe(allData => {
      if (allData) {
        this.exportForm.patchValue({ dateFrom: null, dateTo: null });
      }
    });

    this.exportForm.get('type').valueChanges.subscribe(type => {
      if (type === 'generic-diary') {
        this.diaryFormService.list(this.trial.id).subscribe({
          next: forms => {
            this.diaryForms = forms;
            this.exportForm.patchValue({ diaryFormId: forms.length > 0 ? forms[0].id : null });
          },
          error: () => {
            this.diaryForms = [];
            this.exportForm.patchValue({ diaryFormId: null });
          }
        });
      }
      else {
        this.diaryForms = [];
        this.exportForm.patchValue({ diaryFormId: null });
      }
    });

    this.store.dispatch(new UpdateLastRouteAction(this.location.path()));
  }

  ngAfterViewInit() {
  }

  checkFragmentAndRedirect() {
    this.activatedRoute.fragment.subscribe(fragment => {
      if (fragment) {
        this.selectedTab = fragment;
      }
    });

    // Use the nav service to get the last nav state and check if it matches the current trial id.
    const navState = this.navService.get(NavRoute.Trial);
    if (navState && navState.fragment && navState.lastId === this.trial.id) {
      this.selectedTab = navState.fragment;
    }
  }

  /**
   * Loads a trial and populates a local property
   * @param trialId
   */
  loadTrial(trialId: string) {
    this.dataLoaded = false;
    this.trialService.retrieveTrial(trialId).subscribe({
      next: (trial) => {
        this.trial = trial;
        this.checkFragmentAndRedirect();

        this.setExportOptions();

        this.dataLoaded = true;
      },
      error: (error) => {
        this.dataLoaded = true;
        LogHelper.log(error);
        if (error.status === 403) {
          this.alertService.showWarningAlert('You do not have permission to view this trial.');
          void this.router.navigate(['/trial']);
        } else {
          this.alertService.showWarningAlert(`Unable to load trial: ${error.error.title}`);
        }
      }
    });
  }

  onUserRoleChanged(updatedUser: TrialAdminUser) {
    let user = this.trial.trialUsers.find(user => user.id === updatedUser.id);
    if (!user)
      return;

    user.projectManager = updatedUser.projectManager;
    user.designatedContact = updatedUser.designatedContact;
  }

  onConfirmSelectTab(tabName: string) {
    this.supportComponent.setContactsHasChanges(false);
    this.contactModal.hide();
    this.selectedTab = tabName;
    this.preSelectedTab = '';

    // Track the new tab first so that checkFragmentAndRedirect (called inside loadTrial)
    // reads the correct fragment when the trial data reloads
    this.navService.track({ route: NavRoute.Trial, lastId: this.trial.id, fragment: tabName });

    // Reload trial data to discard unsaved support contact changes
    this.loadTrial(this.trial.id);
  }

  onSelectTab(tabName: string) {
    this.preSelectedTab = tabName;
    if (this.selectedTab === 'support' && this.supportContactsHasChanged) {
      this.selectedTab = 'support';
      this.contactModal.show();
    } else {
      this.selectedTab = tabName;
      // Track that the user has changed tab
      this.navService.track({route: NavRoute.Trial, lastId: this.trial.id, fragment: tabName});
    }
  }

  /**
   * Updates the angular form when you mouse over the export button, it pulls the dates from the fields and puts them
   * into the form. It's a bodge to fix an issue with the jQuery datepicker and angular
   */
  updateExportFormValues() {
    this.exportForm.patchValue({
      dateFrom: this.dateFrom.nativeElement.value,
      dateTo: this.dateTo.nativeElement.value
    });
  }

  private setExportOptions() {
    this.exportOptions = [];
    const canExportDiary = this.authService.hasPermission(Permissions.TrialExportDiaryData);

    if (this.authService.hasPermission(Permissions.TrialExportTripData))
    {
      this.exportOptions.push({ value: 'default', text: 'Default trial data' });
    }

    if (this.trial.diaryEnabled && canExportDiary)
    {
      this.exportOptions.push({ value: 'diary', text: 'Maze e-diary' });
    }

    if (this.trial.genericDiaryEnabled && canExportDiary)
    {
      this.exportOptions.push({ value: 'generic-diary', text: 'E-diary' });
    }
  }

  onShowTripExportModal() {
    this.exportForm.patchValue({
      dateFrom: '',
      dateTo: '',
      type: this.exportOptions[0]?.value ?? null,
      site: '',
      allData: false,
      allSites: false,
      diaryFormId: null
    });
    this.diaryForms = [];

    this.exportTripsModal.show();
  }

  onExportChangeLog() {
    this.exportIsProcessing = true;
    const from = this.exportForm.get('dateFrom').value;
    const to = this.exportForm.get('dateTo').value;

    this.trialService.exportTrips(
      this.trial.id,
      from,
      to,
      this.exportForm.get('type').value,
      this.exportForm.get('site').value,
      this.exportForm.get('diaryFormId').value
    ).subscribe({
      next: () => {
        this.alertService.showSuccessAlert('The export request was sent, please check your email.');
        this.exportTripsModal.hide();
        this.exportIsProcessing = false;
      },
      error: error => {
        LogHelper.log(error);
        this.exportIsProcessing = false;
        this.exportTripsModal.hide();
        this.alertService.showWarningAlert('There was a problem requesting the export.');
      }
    });
  }

  formatUrl(link: string): string {
    if (!link.startsWith('http://') && !link.startsWith('https://')) {
      return 'https://' + link;
    }
    return link;
  }

  protected readonly BadgeStyle = BadgeStyle;
  protected readonly BadgeSize = BadgeSize;
}
